import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { Database } from "@/integrations/supabase/types";
import { setSentryRoles } from "@/lib/sentry";

type AppRole = Database["public"]["Enums"]["app_role"];

export const useUserRole = () => {
  const { user } = useAuth();

  const { data: roles, isLoading: queryLoading } = useQuery<AppRole[]>({
    queryKey: ["user-roles", user?.id],
    queryFn: async (): Promise<AppRole[]> => {
      if (!user) return [];

      // Mirror the `has_role()` SECURITY DEFINER function exactly:
      // only roles where active = true grant access via RLS, so the
      // client-side guards must use the same filter.
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("active", true);

      if (error) throw error;
      // Always return an explicit array — never undefined — so consumers
      // can rely on `.includes()` without optional chaining.
      return (data ?? []).map((r) => r.role as AppRole);
    },
    enabled: !!user,
  });

  // When the query is `enabled: !!user` but the user hasn't loaded yet,
  // react-query reports isLoading=false even though we have no data.
  // Treat "user present but roles still undefined" as loading so that
  // route guards don't fire a premature redirect.
  const isLoading = user ? (queryLoading || roles === undefined) : false;

  // Explicit empty-array contract: downstream guards never see undefined.
  const safeRoles: AppRole[] = roles ?? [];

  useEffect(() => {
    setSentryRoles(safeRoles as string[]);
  }, [safeRoles.join(",")]);

  return {
    roles: safeRoles,
    isLoading,
    isSystemOwner: safeRoles.includes("system_owner"),
    isAdmin: safeRoles.includes("admin"),
    isCompanyOwner: safeRoles.includes("company_owner"),
    isEmployee: safeRoles.includes("employee"),
  };
};